import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import LandingPage, {
    type ILandingPageFeatureCard,
    type ILandingPageQuickLink,
    type ILandingPageWidget,
} from '@src/App/Components/Core/Layouts/LandingPage/LandingPage';
import MvpSubNav, { type IMvpQuickAction, type IMvpRole } from '@src/App/Components/Core/MvpSubNav/MvpSubNav';
import { EIconCategory, getAppIcon } from '@src/helper/iconRegistry';
import type { AppState } from '@store/store';
import { Award, BarChart3, Building2, Calendar, Target, TrendingUp, UserPlus, Users } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

function UnternehmenLandingPage() {
    const theme = useTheme();
    const navigate = useNavigate();
    const allEmployees = useSelector((state: AppState) => state.company.allEmployees);
    const orgUnits = useSelector((state: AppState) => state.OUs);
    const employees = useSelector((state: AppState) => state.employees);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    const [activeRole, setActiveRole] = useState('admin');

    // Company Statistics
    const companyStats = useMemo(() => {
        const totalEmployees = allEmployees.length;
        const totalOrgUnits = orgUnits.length;

        const employeesWithAvailability = employees.filter((emp) => emp.availability !== undefined);
        const avgAvailability =
            employeesWithAvailability.length > 0
                ? Math.round(
                      employeesWithAvailability.reduce((sum, emp) => {
                          const availability =
                              typeof emp.availability === 'number'
                                  ? emp.availability
                                  : emp.availability?.availability || 0;
                          return sum + availability;
                      }, 0) / employeesWithAvailability.length,
                  )
                : 0;

        const allSkills = employees.flatMap((emp) => emp.skills || []);
        const skillCounts = allSkills.reduce(
            (acc, skill) => {
                const skillName = skill.title || 'Unbekannt';
                acc[skillName] = (acc[skillName] || 0) + 1;
                return acc;
            },
            {} as Record<string, number>,
        );

        const topSkills = Object.entries(skillCounts)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 5)
            .map(([skill, count]) => ({ skill, count }));

        const activeProjects = employees.reduce(
            (sum, emp) => sum + (emp.projects?.filter((p) => p.active)?.length || 0),
            0,
        );

        return {
            totalEmployees,
            totalOrgUnits,
            avgAvailability,
            topSkills,
            activeProjects,
            availableEmployees: employeesWithAvailability.filter((emp) => {
                const availability =
                    typeof emp.availability === 'number' ? emp.availability : emp.availability?.availability || 0;
                return availability >= 60;
            }).length,
        };
    }, [allEmployees, orgUnits, employees]);

    const roles: IMvpRole[] = [
        {
            id: 'admin',
            label: 'Admin',
            icon: getAppIcon(EIconCategory.SETTINGS),
        },
        {
            id: 'hr',
            label: 'HR',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
        },
    ];

    const quickActions: IMvpQuickAction[] = [
        {
            id: 'employees',
            label: 'Mitarbeitende',
            icon: getAppIcon(EIconCategory.EMPLOYEES),
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
            variant: 'primary',
        },
        {
            id: 'org-units',
            label: 'Abteilungsbereiche',
            icon: getAppIcon(EIconCategory.DEPARTMENTS),
            onClick: () => navigate(`/${ELinks.PEOPLE_ORGUNITS}`),
        },
    ];

    const featureCards: ILandingPageFeatureCard[] = [
        {
            id: 'employee-overview',
            title: 'Mitarbeiter-Übersicht',
            description: 'Vollständige Übersicht aller Mitarbeitenden mit Skills, Verfügbarkeiten und Projekten',
            icon: <Users size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=200&fit=crop',
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
        },
        {
            id: 'org-structure',
            title: 'Organisationsstruktur',
            description: 'Hierarchische Darstellung aller Abteilungen und Organisationseinheiten',
            icon: <Building2 size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=200&fit=crop',
            onClick: () => navigate(`/${ELinks.PEOPLE_ORGUNITS}`),
        },
        {
            id: 'skill-matrix',
            title: 'Unternehmens-Skill-Matrix',
            description: 'Analyse der Kompetenzverteilung und Identifikation von Skill-Gaps im Unternehmen',
            icon: <BarChart3 size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=200&fit=crop',
            onClick: () => console.log('Company Skill Matrix'),
        },
        {
            id: 'availability-dashboard',
            title: 'Verfügbarkeits-Dashboard',
            description: 'Echtzeit-Übersicht der Mitarbeiterverfügbarkeit für optimale Ressourcenplanung',
            icon: <Calendar size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1611224923853-80b023f02d71?w=400&h=200&fit=crop',
            onClick: () => console.log('Availability Dashboard'),
        },
        {
            id: 'development-tracking',
            title: 'Entwicklung & Onboarding',
            description: 'Verfolgung von Mitarbeiterentwicklung, Onboarding-Prozessen und Weiterbildungen',
            icon: <TrendingUp size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=200&fit=crop',
            onClick: () => console.log('Development Tracking'),
        },
        {
            id: 'reporting-analytics',
            title: 'Reporting & Analytics',
            description: 'Detaillierte Berichte und Analysen zu Personalmetriken und Unternehmenskennzahlen',
            icon: <Award size={24} />,
            imagePath: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=200&fit=crop',
            onClick: () => console.log('Reporting Analytics'),
        },
    ];

    const quickLinks: ILandingPageQuickLink[] = [
        {
            id: 'employees',
            title: 'Alle Mitarbeitenden',
            description: 'Alle Mitarbeitenden anzeigen, bearbeiten und organisieren',
            icon: <Users size={28} />,
            variant: 'primary',
            onClick: () => navigate(`/${ELinks.PEOPLE_EMPLOYEES}`),
        },
        {
            id: 'org-units',
            title: 'Abteilungsbereiche',
            description: 'Organisationsstruktur und Abteilungen konfigurieren',
            icon: <Building2 size={28} />,
            variant: 'secondary',
            onClick: () => navigate(`/${ELinks.PEOPLE_ORGUNITS}`),
        },
    ];

    const renderCompanyStatsWidget = () => (
        <Stack spacing={3}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Chip
                    label={`${companyStats.totalEmployees} Mitarbeitende`}
                    color="primary"
                    variant="outlined"
                    size="medium"
                />
                <Chip
                    label={`${companyStats.totalOrgUnits} Abteilungen`}
                    color="secondary"
                    variant="outlined"
                    size="medium"
                />
                <Chip
                    label={`${companyStats.avgAvailability}% Ø Verfügbarkeit`}
                    color="info"
                    variant="outlined"
                    size="medium"
                />
                <Chip
                    label={`${companyStats.availableEmployees} verfügbar`}
                    color="success"
                    variant="outlined"
                    size="medium"
                />
                <Chip
                    label={`${companyStats.activeProjects} aktive Projekte`}
                    color="warning"
                    variant="outlined"
                    size="medium"
                />
            </Box>

            {companyStats.topSkills.length > 0 && (
                <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                        Top Skills im Unternehmen
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {companyStats.topSkills.map((skillData, index) => (
                            <Chip
                                key={skillData.skill}
                                label={`${skillData.skill} (${skillData.count})`}
                                size="small"
                                variant="filled"
                                color={index === 0 ? 'primary' : 'default'}
                                sx={{
                                    opacity: index === 0 ? 1 : 0.8 - index * 0.1,
                                    fontWeight: index === 0 ? 600 : 400,
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Stack>
    );

    const widgets: ILandingPageWidget[] = [
        {
            id: 'company-stats',
            title: 'Unternehmens-Statistiken',
            content: renderCompanyStatsWidget(),
        },
    ];

    return (
        <>
            <MvpSubNav
                currentPageTitle="Unternehmen"
                roles={roles}
                activeRole={activeRole}
                onRoleChange={setActiveRole}
                quickActions={quickActions}
            />

            <LandingPage
                title="Unternehmen"
                subtitle="Menschen & Organisationsstruktur im Überblick"
                description="Verwalten Sie Ihr Unternehmen effizient mit intelligenten Tools für Personalmanagement, Organisationsstruktur und strategische Personalplanung."
                heroIcon={
                    <Box
                        component="img"
                        src="/img/t4lo_companyillu.png"
                        alt="Company Illustration"
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            boxShadow: 'none',
                            filter: 'none',
                            objectFit: 'contain',
                            objectPosition: 'center',
                        }}
                    />
                }
                processTitle="Intelligentes Unternehmensmanagement"
                featureCards={featureCards}
                quickLinksTitle="Schnellzugriff"
                quickLinks={quickLinks}
                widgets={widgets}
            />
        </>
    );
}

export default UnternehmenLandingPage;
